import { ReactNode, useEffect, useRef, useState } from "react";
import {
  BRACKET_INNER,
  PLACEHOLDER_INNER,
  SCRIPT_TOKEN_REGEX,
  Segment,
  currentSegmentIdx,
  emojify,
} from "../utils";

type Phase = "ready" | "countdown" | "recording";

interface Props {
  /** Lines shown before recording (above `---` in the script). */
  intro: string[];
  /** Recording-time segments (below `---`, or all lines if no separator). */
  segments: Segment[];
  /** Per-segment timing computed from durations + maxSec. */
  timing: { start: number; dur: number }[];
  /** Elapsed seconds in the recording (0 before REC starts). */
  elapsedSec: number;
  /** Current recorder phase — drives intro vs segments rendering. */
  phase: Phase;
}

const LEMONADE = "#FFFC7D";
const KARAOKE_TRACK = "#808080"; // ash — the unfilled portion of the underline
// Target word count per phrase group in "phrase" karaoke mode. Punctuation
// and newlines force a break earlier; an orphan single-word final group is
// merged into the previous one to avoid a lone end-of-segment flash.
const PHRASE_TARGET_WORDS = 3;

/**
 * Segment-based teleprompter.
 *
 * Inline syntax (per line):
 * - `...(text)...` → lemonade-yellow placeholder.
 * - `[text]`       → orange highlight.
 * - Emoticons (`:)`, `;)`, `:p`, `XD`, `<3`, etc.) → unicode emoji.
 *
 * Segment markers (start of line):
 * - `(N)` / `(N.M)` → explicit duration in seconds.
 * - `()`            → auto duration (share remaining time by word count).
 * - `*(N)` / `*()`  → karaoke "phrase" mode: text appears all at once; the
 *                     underline is a continuous bar PER GROUP (~3 words per
 *                     group, punctuation/newlines force an earlier break)
 *                     that fills yellow over an ash-grey track during the
 *                     groups time slot. Past groups stay full yellow; future
 *                     groups have no track until their turn — a classic
 *                     karaoke fill cadence.
 * - `**(N)` / `**()`→ karaoke "instant" mode: full underline appears under
 *                     every word the moment the segment becomes active.
 */
export default function Teleprompter({
  intro,
  segments,
  timing,
  elapsedSec,
  phase,
}: Props) {
  // Hooks first — must run unconditionally to keep order stable across phases.
  const idx = Math.max(0, currentSegmentIdx(elapsedSec, timing));
  const seg = segments[idx] as Segment | undefined;
  const t = timing[idx] ?? { start: 0, dur: 1 };
  const segElapsed = Math.max(0, elapsedSec - t.start);
  const karaokeMode: Segment["karaoke"] | undefined =
    phase === "recording" && t.dur > 0 ? seg?.karaoke : undefined;
  const needsRaf = karaokeMode === "phrase";

  // Capture wall-clock at segment start so we can interpolate sub-second
  // progress between the parent's 1Hz elapsedSec ticks.
  const segOriginWallMs = useRef(performance.now());
  const segOriginSegEl = useRef(0);
  useEffect(() => {
    segOriginWallMs.current = performance.now();
    segOriginSegEl.current = Math.max(0, elapsedSec - t.start);
    // We only want this to fire when the segment changes — capturing the
    // current elapsed AT that moment. Re-running on every elapsedSec tick
    // would reset the wall clock and stutter the underline.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  // RAF loop, active only during a phrase-karaoke segment.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!needsRaf) return;
    let raf = 0;
    const loop = () => {
      setTick((n) => (n + 1) & 0xffff);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [needsRaf, idx]);

  const liveSegEl = needsRaf
    ? segOriginSegEl.current +
      (performance.now() - segOriginWallMs.current) / 1000
    : segElapsed;
  const liveSegFrac =
    t.dur > 0 ? Math.min(1, Math.max(0, liveSegEl / t.dur)) : 0;

  // ── Intro mode (ready / countdown) ─────────────────────────────────
  if (phase !== "recording") {
    const lines = intro.length > 0 ? intro : segments.slice(0, 1).map((s) => s.raw);
    if (lines.length === 0) return null;
    return (
      <div className="flex flex-col items-center justify-center h-full px-6 gap-3">
        {lines.map((line, i) => (
          <div
            key={i}
            className="text-xl md:text-3xl text-ivory/95 font-medium text-center leading-snug max-w-3xl whitespace-pre-line"
          >
            {renderLine(line)}
          </div>
        ))}
      </div>
    );
  }

  // ── Recording mode — active segment based on elapsed time ──────────
  if (segments.length === 0 || !seg) return null;
  const segmentProgress = t.dur > 0 ? Math.min(1, segElapsed / t.dur) : 1;

  return (
    <div className="flex flex-col items-center justify-center h-full px-6 gap-4">
      {segments.length > 1 && (
        <div className="w-full max-w-md flex items-center gap-3">
          <span className="text-ivory/60 text-xs font-mono tabular-nums">
            {idx + 1}/{segments.length}
          </span>
          <div className="flex-1 h-1 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-lemonade transition-[width] duration-100 ease-linear"
              style={{ width: `${segmentProgress * 100}%` }}
            />
          </div>
        </div>
      )}
      <div
        key={idx}
        className="teleprompter-fade text-2xl md:text-4xl text-ivory font-semibold text-center leading-snug max-w-3xl whitespace-pre-line"
      >
        {karaokeMode
          ? renderKaraokeLine(seg.raw, liveSegFrac, karaokeMode)
          : renderLine(seg.raw)}
      </div>
    </div>
  );
}

/** Render one line: emojify, then tokenize for placeholders + brackets. */
function renderLine(rawLine: string): ReactNode {
  const line = emojify(rawLine);
  const parts = line.split(SCRIPT_TOKEN_REGEX).filter(Boolean);
  return parts.map((part, i) => {
    const ph = part.match(PLACEHOLDER_INNER);
    if (ph) {
      return (
        <span key={i} className="text-lemonade font-bold">
          {ph[1]}
        </span>
      );
    }
    const br = part.match(BRACKET_INNER);
    if (br) {
      return (
        <span key={i} className="text-orange-400 font-bold">
          {br[1]}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

type WordUnit = {
  kind: "word";
  text: string;
  color: "plain" | "lemonade" | "orange";
  weight: number;
};
type SpaceUnit = { kind: "space"; text: string };
type KaraokeUnit = WordUnit | SpaceUnit;
type PhraseGroup = { units: KaraokeUnit[]; weight: number };

/** Karaoke rendering. Text appears all at once; the underline is driven by
 *  `segFrac` (0..1) per `mode`.
 *
 *  - `mode === "instant"` → every word receives a full lemonade underline
 *    immediately; signals "say this whole line at once". No animation.
 *  - `mode === "phrase"`  → words are split into groups of roughly
 *    PHRASE_TARGET_WORDS, with punctuation/newlines forcing an earlier
 *    break. For each group we render a **single continuous underline bar**
 *    spanning all words+spaces of the group. While the group is the active
 *    one, the bar shows an ash-grey track with a lemonade fill that grows
 *    left→right over the groups time slot. Past groups stay fully lemonade,
 *    future groups have no bar at all (it appears only when their slot
 *    starts). */
function renderKaraokeLine(
  rawLine: string,
  segFrac: number,
  mode: "phrase" | "instant",
): ReactNode {
  const line = emojify(rawLine);
  const parts = line.split(SCRIPT_TOKEN_REGEX).filter(Boolean);

  // Step 1: tokenize the line into word + space units, preserving the
  //         lemonade/orange highlight categories of placeholder & bracket
  //         tokens for downstream colour styling.
  const units: KaraokeUnit[] = [];
  const wordRe = /(\s+|\S+)/g;
  for (const part of parts) {
    const ph = part.match(PLACEHOLDER_INNER);
    const br = part.match(BRACKET_INNER);
    let color: WordUnit["color"] = "plain";
    let inner = part;
    if (ph) {
      color = "lemonade";
      inner = ph[1];
    } else if (br) {
      color = "orange";
      inner = br[1];
    }
    wordRe.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = wordRe.exec(inner)) !== null) {
      const tok = m[0];
      if (/^\s+$/.test(tok)) {
        units.push({ kind: "space", text: tok });
      } else {
        units.push({
          kind: "word",
          text: tok,
          color,
          weight: Math.max(1, tok.length),
        });
      }
    }
  }

  // Step 2: group consecutive units into phrase chunks of ~PHRASE_TARGET_WORDS
  //         words. Boundaries (force an earlier break): a word that ends with
  //         punctuation, a space unit that contains a newline, or reaching
  //         the target word count. Each group carries its summed weight so
  //         longer chunks get a proportionally larger time slot.
  const groups: PhraseGroup[] = [];
  let curUnits: KaraokeUnit[] = [];
  let curWeight = 0;
  let curWords = 0;
  const flushGroup = () => {
    if (curUnits.length === 0) return;
    groups.push({ units: curUnits, weight: Math.max(1, curWeight) });
    curUnits = [];
    curWeight = 0;
    curWords = 0;
  };
  for (const u of units) {
    if (u.kind === "word") {
      curUnits.push(u);
      curWeight += u.weight;
      curWords += 1;
      if (/[,;:.!?…]$/.test(u.text) || curWords >= PHRASE_TARGET_WORDS) {
        flushGroup();
      }
    } else {
      if (u.text.includes("\n") && curUnits.length > 0) flushGroup();
      curUnits.push(u);
    }
  }
  flushGroup();

  // Merge a final orphan group (1 lone word) into the previous group so the
  // last flash isn't a single word dangling on its own.
  if (groups.length >= 2) {
    const last = groups[groups.length - 1];
    const lastWordCount = last.units.filter((u) => u.kind === "word").length;
    if (lastWordCount === 1) {
      const prev = groups[groups.length - 2];
      prev.units = [...prev.units, ...last.units];
      prev.weight += last.weight;
      groups.pop();
    }
  }

  // Step 3: per-group time slots within the segment.
  const totalWeight = groups.reduce((s, g) => s + g.weight, 0) || 1;
  let cum = 0;
  const groupSlots = groups.map((g) => {
    const startFrac = cum / totalWeight;
    cum += g.weight;
    return { startFrac, endFrac: cum / totalWeight };
  });

  const groupState = (
    gi: number,
  ): { progress: number; visible: boolean } => {
    if (mode === "instant") return { progress: 1, visible: true };
    const { startFrac, endFrac } = groupSlots[gi];
    if (segFrac < startFrac) return { progress: 0, visible: false };
    if (segFrac >= endFrac) return { progress: 1, visible: true };
    const slot = endFrac - startFrac;
    if (slot <= 0) return { progress: 1, visible: true };
    return { progress: (segFrac - startFrac) / slot, visible: true };
  };

  // Step 4: emit ONE wrapper span per group, with the two-layer underline
  //         bar (yellow + grey) drawn across all its words+spaces. The yellow
  //         layer size is driven by `progress`; the grey track is full width
  //         when visible and absent for future groups.
  return groups.map((g, gi) => {
    const { progress, visible } = groupState(gi);
    const groupStyle: React.CSSProperties = visible
      ? {
          // Yellow fill listed first so it stacks on top of the grey track.
          backgroundImage: `linear-gradient(${LEMONADE}, ${LEMONADE}), linear-gradient(${KARAOKE_TRACK}, ${KARAOKE_TRACK})`,
          backgroundSize: `${progress * 100}% 3px, 100% 3px`,
          backgroundPosition: "left bottom, left bottom",
          backgroundRepeat: "no-repeat, no-repeat",
          paddingBottom: "4px",
          // Clone the background across line-wrap boundaries so a multi-line
          // group keeps the bar on each visual line. iOS Safari needs the
          // webkit-prefixed property.
          WebkitBoxDecorationBreak: "clone",
          boxDecorationBreak: "clone",
        }
      : {};
    return (
      <span key={gi} style={groupStyle}>
        {g.units.map((u, ui) => {
          if (u.kind === "space") return <span key={ui}>{u.text}</span>;
          const colorClass =
            u.color === "lemonade"
              ? "text-lemonade font-bold"
              : u.color === "orange"
              ? "text-orange-400 font-bold"
              : "";
          return (
            <span key={ui} className={colorClass}>
              {u.text}
            </span>
          );
        })}
      </span>
    );
  });
}
